import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REASON_CODES, REASON_CODE_LABELS } from "@/admin/types/admin";

interface Props {
  open: boolean;
  title: string;
  reasonCategory: keyof typeof REASON_CODES;
  onConfirm: (reasonCode: string, notes?: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ReasonModal({ open, title, reasonCategory, onConfirm, onCancel, loading }: Props) {
  const [reasonCode, setReasonCode] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const codes = REASON_CODES[reasonCategory] ?? [];

  const handleConfirm = () => {
    if (!reasonCode) return;
    onConfirm(reasonCode, notes.trim() || undefined);
    setReasonCode("");
    setNotes("");
  };

  const handleCancel = () => {
    setReasonCode("");
    setNotes("");
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="reason-select">Motif *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger id="reason-select">
                <SelectValue placeholder="Sélectionner un motif..." />
              </SelectTrigger>
              <SelectContent>
                {codes.map((code) => (
                  <SelectItem key={code} value={code}>
                    {REASON_CODE_LABELS[code] ?? code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes-textarea">Notes (optionnel)</Label>
            <Textarea
              id="notes-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={!reasonCode || loading}>
            {loading ? "En cours..." : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
